import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Stack,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  TablePagination,
  TextField,
  InputAdornment,
  TableSortLabel,
  Tabs,
  Tab,
  Button
} from "@mui/material";
import { AiOutlineClose, AiOutlineSearch, AiOutlineInfoCircle } from "react-icons/ai";
import { PiFileCsv } from "react-icons/pi";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { fetchCircuitReport } from "../../api/dashboardApi";
import { useNavigate } from "react-router-dom";

/**
 * Simple, layman-friendly tooltip descriptions for Circuit Report columns.
 */
const CIRCUIT_REPORT_TOOLTIPS = {
  "S.No.": "Row number in the report.",
  "Office": "Main office location — usually the city name.",
  "Location": "Full location name tied to the circuit.",
  "PIP Circuit ID": "Unique ID for the circuit.",
  "Service ID": "Unique ID for the service on this circuit.",
  "Service": "Name of the service (e.g., Internet, MPLS).",
  "MRC": "Monthly Recurring Charge — the standard monthly fee from the latest invoice.",
  "Contract Amount": "Agreed monthly amount as per the contract.",
  "Disconnect Request Number": "Reference number for the disconnection request.",
  "Is Removed": "Whether this service has been disconnected — Yes or No.",
  "Removed Date": "Date when the service was officially removed.",
  "Removed SOF": "SOF document that authorized the removal.",
  "Ref From": "Source type of the reference (e.g., contract, order).",
  "Ref Doc ID": "ID of the matched reference document.",
  "Ref Ref ID": "Additional reference number from the matched document.",
  "Submission Date": "Date the request or report was submitted.",
  "Status": "Current status of the circuit in the processing queue.",
  "Months": "Number of months of billing data available.",
  // Monthly sub-table tooltips
  "Month": "The billing month.",
  "Amount": "Total amount billed for this service during that month.",
  "Contract Amount Monthly": "Contract value tied to invoices in that month.",
  "Charge Type": "Type of charge that month (e.g., recurring, adjustment, setup fee).",
  "Invoice ID": "Invoice number(s) containing charges for that month.",
};

/**
 * Renders a column header label with an info-icon tooltip.
 */
const HeaderWithTooltip = ({ label, tooltipKey, sx = {} }) => {
  const tip = CIRCUIT_REPORT_TOOLTIPS[tooltipKey || label];
  if (!tip) return label;
  return (
    <Tooltip
      title={
        <Typography variant="caption" sx={{ lineHeight: 1.3 }}>
          {tip}
        </Typography>
      }
      arrow
      placement="top"
    >
      <span style={{ cursor: "help" }}>
        {label}
      </span>
    </Tooltip>
  );
};

/**
 * Expandable row for the primary table.
 */
const ServiceRow = ({ item }) => {
  const [open, setOpen] = useState(false);
  const { removed_service: service, matching_records: matching, match_count: count } = item;

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          "&:hover": { backgroundColor: "#fcf9ff" },
        }}
      >
        <TableCell sx={{ width: 50 }}>
          {count > 0 && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
              sx={{ color: "#7C2EA0" }}
            >
              {open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{service.service_id}</TableCell>
        <TableCell>{service.service_name}</TableCell>
        <TableCell sx={{ maxWidth: 150 }}>
          <Tooltip title={service.service_description} arrow>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontSize: "13px",
              }}
            >
              {service.service_description}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip title={service.location_name} arrow>
            <Typography variant="body2" sx={{ fontSize: "13px", cursor: "help", borderBottom: "1px dashed #ccc", display: "inline-block" }}>
              {service.location_id}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>{service.doc_id}</TableCell>
        <TableCell>{service.quantity}</TableCell>
        <TableCell>
          <Tooltip title={service.service_description} arrow>
            <Typography variant="body2" sx={{ fontSize: "13px", cursor: "help", borderBottom: "1px dashed #7C2EA0", display: "inline-block", color: "#7C2EA0", fontWeight: 500 }}>
              {service.service_commitment}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>{service.mrc}</TableCell>
        <TableCell>{service.nrc}</TableCell>
        <TableCell sx={{ fontWeight: 600, color: "#7C2EA0" }}>{service.converted_mrc_usd}</TableCell>
        <TableCell sx={{ fontWeight: 600, color: "#7C2EA0" }}>{service.converted_nrc_usd}</TableCell>
        <TableCell>
          {(() => {
            const val = service.conversion_rate;
            if (val === null || val === undefined || val === "N/A" || val === "") return "N/A";
            const num = Number(val);
            if (isNaN(num)) return val;
            const formatted = parseFloat(num.toFixed(3)).toString();
            return (
              <Tooltip title={String(val)} arrow placement="top">
                <Box component="span" sx={{ cursor: "pointer", borderBottom: "1px dashed #7C2EA0" }}>
                  {formatted}
                </Box>
              </Tooltip>
            );
          })()}
        </TableCell>
        <TableCell>{service.currency}</TableCell>
        <TableCell>{service.country}</TableCell>
        <TableCell>{service.sof_date || "N/A"}</TableCell>
        <TableCell>{service.ref_id}</TableCell>
        <TableCell>{service.row_flag}</TableCell>
        <TableCell>
          <Box
            component="span"
            sx={{
              color: "#d32f2f",
              fontWeight: 700,
              fontSize: "11px",
              backgroundColor: "#ffebee",
              px: 1,
              py: 0.5,
              borderRadius: "4px",
              textTransform: "uppercase"
            }}
          >
            {service.activity}
          </Box>
        </TableCell>
        <TableCell align="right">
          <Box
            component="span"
            sx={{
              backgroundColor: count > 0 ? "#e8f5e9" : "#f5f5f5",
              color: count > 0 ? "#2e7d32" : "#757575",
              padding: "2px 10px",
              borderRadius: "12px",
              fontSize: "11px",
              fontWeight: 700,
              border: count > 0 ? "1px solid #2e7d32" : "1px solid #bdbdbd",
              whiteSpace: "nowrap"
            }}
          >
            {count} {count === 1 ? "Match" : "Matches"}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={21}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 2,
                backgroundColor: "#fdfbff",
                borderRadius: "8px",
                p: 2,
                border: "1px solid #f0e6f5",
                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1.5,
                  fontWeight: 700,
                  color: "#7C2EA0",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                Matching Records Found in Other Documents
                {/* <Tooltip title="Displays records from other SOF documents that match the selected removed service based on Service ID, Service Name, Location ID, and Service Description. These matching records help identify whether the service still exists in another SOF document." arrow placement="right">
                  <span style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <AiOutlineInfoCircle size={16} />
                  </span>
                </Tooltip> */}
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eee" }}>
                <Table size="small" aria-label="matching-records">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>Service ID</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>Service Name</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>Doc ID</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>Qty</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>Commitment</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>MRC</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>NRC</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>USD MRC</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>USD NRC</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>Conversion Rate</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>Currency</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>Country</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>SOF Date</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>Ref ID</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>Flag</TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>Activity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matching.map((m, idx) => (
                      <TableRow key={idx} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                        <TableCell sx={{ fontSize: "12px" }}>{m.service_id}</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>{m.service_name}</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          <Tooltip title={m.location_name} arrow>
                            <Box sx={{ borderBottom: "1px dashed #ccc", cursor: "help" }}>{m.location_id}</Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>{m.doc_id}</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>{m.quantity}</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          <Tooltip title={m.service_description} arrow>
                             <Box sx={{ color: "#7C2EA0", fontWeight: 500, borderBottom: "1px dashed #7C2EA0", cursor: "help" }}>{m.service_commitment}</Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>{m.mrc}</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>{m.nrc}</TableCell>
                        <TableCell sx={{ fontSize: "12px", color: '#7C2EA0', fontWeight: 600 }}>{m.converted_mrc_usd}</TableCell>
                        <TableCell sx={{ fontSize: "12px", color: '#7C2EA0', fontWeight: 600 }}>{m.converted_nrc_usd}</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          {(() => {
                            const val = m.conversion_rate;
                            if (val === null || val === undefined || val === "N/A" || val === "") return "N/A";
                            const num = Number(val);
                            if (isNaN(num)) return val;
                            const formatted = parseFloat(num.toFixed(3)).toString();
                            return (
                              <Tooltip title={String(val)} arrow placement="top">
                                <Box component="span" sx={{ cursor: "pointer", borderBottom: "1px dashed #7C2EA0" }}>
                                  {formatted}
                                </Box>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>{m.currency}</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>{m.country}</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>{m.sof_date || "N/A"}</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>{m.ref_id}</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>{m.row_flag}</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          <Box
                            component="span"
                            sx={{
                              color: "#2e7d32",
                              fontWeight: 700,
                              backgroundColor: "#e8f5e9",
                              px: 1,
                              py: 0.3,
                              borderRadius: "4px",
                              textTransform: "uppercase"
                            }}
                          >
                            {m.activity}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

/**
 * Expandable row for circuit report data with months breakdown
 */
const CircuitReportRow = ({ item }) => {
  const [open, setOpen] = useState(false);
  const [contractViewTab, setContractViewTab] = useState('grouped');
  const months = item?.months || [];
  const hasMonths = months.length > 0;

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          "&:hover": { backgroundColor: "#fcf9ff" },
        }}
      >
        <TableCell sx={{ width: 50 }}>
          {hasMonths && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
              sx={{ color: "#7C2EA0" }}
            >
              {open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>{item["S.No."]}</TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{item["Office"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px" }}>
          <Tooltip title={item["Location"]} arrow>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 150,
                fontSize: "12px",
              }}
            >
              {item["Location"] || "N/A"}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>{item["PIP Circuit ID"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{item["Service ID"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{item["Service"] || "N/A"}</TableCell>
        {/* <TableCell sx={{ fontSize: "12px" }}>{item["MRC"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{item["Contract Amount"] || "N/A"}</TableCell> */}
        <TableCell sx={{ fontSize: "12px" }}>{item["Disconnect Request Number"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px" }}>
          <Box
            component="span"
            sx={{
              backgroundColor: item["Is Removed"] === "Yes" ? "#ffebee" : "#e8f5e9",
              color: item["Is Removed"] === "Yes" ? "#d32f2f" : "#2e7d32",
              fontWeight: 700,
              fontSize: "11px",
              px: 1,
              py: 0.5,
              borderRadius: "4px",
              textTransform: "uppercase"
            }}
          >
            {item["Is Removed"] || "N/A"}
          </Box>
        </TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{item["Removed Date"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{item["Removed SOF"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{item["Ref From"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{item["Ref Doc ID"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{item["Ref Ref ID"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{item["Submisssion Date"] || "N/A"}</TableCell>
        <TableCell sx={{ fontSize: "12px" }}>{item["Queqe Status/disconnected"] || "N/A"}</TableCell>
        <TableCell align="right">
          <Box
            component="span"
            sx={{
              backgroundColor: hasMonths ? "#e8f5e9" : "#f5f5f5",
              color: hasMonths ? "#2e7d32" : "#757575",
              padding: "2px 10px",
              borderRadius: "12px",
              fontSize: "11px",
              fontWeight: 700,
              border: hasMonths ? "1px solid #2e7d32" : "1px solid #bdbdbd",
              whiteSpace: "nowrap"
            }}
          >
            {months.length} {months.length === 1 ? "Month" : "Months"}
          </Box>
        </TableCell>
      </TableRow>

      {/* Months breakdown row */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={14}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 2,
                backgroundColor: "#fdfbff",
                borderRadius: "8px",
                p: 2,
                border: "1px solid #f0e6f5",
                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)",
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap:1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "#7C2EA0",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  Monthly Invoice Data
                </Typography>
                <Box sx={{ display: 'flex', backgroundColor: '#f4f6f8', borderRadius: '20px', p: '2px' }}>
                  <Box 
                    onClick={(e) => { e.stopPropagation(); setContractViewTab('grouped'); }}
                    sx={{ 
                      px: 1.5, py: 0.5, fontSize: '11px', fontWeight: 600, cursor: 'pointer', borderRadius: '20px',
                      backgroundColor: contractViewTab === 'grouped' ? '#7C2EA0' : 'transparent',
                      color: contractViewTab === 'grouped' ? '#fff' : '#64748b',
                      transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >
                    Grouped
                  </Box>
                  <Box 
                    onClick={(e) => { e.stopPropagation(); setContractViewTab('individual'); }}
                    sx={{ 
                      px: 1.5, py: 0.5, fontSize: '11px', fontWeight: 600, cursor: 'pointer', borderRadius: '20px',
                      backgroundColor: contractViewTab === 'individual' ? '#7C2EA0' : 'transparent',
                      color: contractViewTab === 'individual' ? '#fff' : '#64748b',
                      transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >
                    Individual
                  </Box>
                </Box>
              </Box>
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eee" }}>
                <Table size="small" aria-label="months-data">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}><HeaderWithTooltip label="Month" /></TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}><HeaderWithTooltip label="Amount" /></TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}><HeaderWithTooltip label="NET Amount" /></TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}><HeaderWithTooltip label="Contract Amount" tooltipKey="Contract Amount Monthly" /></TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}><HeaderWithTooltip label="Charge Type" /></TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}><HeaderWithTooltip label="Invoice ID" /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {months.map((month, idx) => (
                      <TableRow key={idx} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                        <TableCell sx={{ fontSize: "12px" }}>{month.month}</TableCell>
                        <TableCell sx={{ fontSize: "12px", fontWeight: 600, color: "#7C2EA0" }}>{month.amount_usd !== undefined ? `\$${Number(month.amount_usd || 0).toFixed(2)}` : "N/A"}</TableCell>
                        <TableCell sx={{ fontSize: "12px", fontWeight: 600, color: "#7C2EA0" }}>{month.net_amount_usd !== undefined ? `\$${Number(month.net_amount_usd || 0).toFixed(2)}` : "N/A"}</TableCell>
                        <TableCell sx={{ fontSize: "12px", fontWeight: 600, color: "#7C2EA0" }}>
                          {contractViewTab === 'grouped' 
                            ? (month.grouped_contract_amount !== undefined ? `\$${Number(month.grouped_contract_amount || 0).toFixed(2)}` : (month.contract_amount || "N/A"))
                            : (month.contract_amount !== undefined ? (String(month.contract_amount).startsWith('$') ? month.contract_amount : `\$${Number(month.contract_amount || 0).toFixed(2)}`) : "N/A")
                          }
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          <Box
                            component="span"
                            sx={{
                              backgroundColor: "#f3e5f5",
                              color: "#7C2EA0",
                              px: 1,
                              py: 0.3,
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: 600,
                            }}
                          >
                            {month.charge_type || "N/A"}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>{month.invoice_id}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const RemovedServicesModal = ({ open, onClose, data, loading, dateRange }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("service_id");
  const [currentTab, setCurrentTab] = useState(0);
  const [circuitSearchQuery, setCircuitSearchQuery] = useState("");
  const [circuitPage, setCircuitPage] = useState(0);
  const [circuitRowsPerPage, setCircuitRowsPerPage] = useState(10);
  const [downloadingCircuitCSV, setDownloadingCircuitCSV] = useState(false);

  // Local date filters for Circuit Report tab (defaults to dashboard dateRange or last 6 months)
  // const initStartDate = dateRange?.startDate
  //   ? dayjs(dateRange.startDate).format("YYYY-MM-DD")
  //   : dayjs().subtract(6, "months").format("YYYY-MM-DD");
  // const initEndDate = dateRange?.endDate
  //   ? dayjs(dateRange.endDate).format("YYYY-MM-DD")
  //   : dayjs().format("YYYY-MM-DD");

  const initStartDate = "2025-09-01";
  const initEndDate = "2026-03-01";
  const [circuitStartDate, setCircuitStartDate] = useState(initStartDate);
  const [circuitEndDate, setCircuitEndDate] = useState(initEndDate);

  // Use react-query to fetch circuit report data (prevents repeated API calls)
  const { data: circuitReportResponse, isLoading: circuitLoading } = useQuery({
    queryKey: ["circuitReport", circuitStartDate, circuitEndDate],
    queryFn: () => fetchCircuitReport({
      start_date: circuitStartDate,
      end_date: circuitEndDate,
      download_csv: false,
    }),
    enabled: open && currentTab === 1, // Only fetch when modal open and Circuit tab active
    staleTime: 60 * 60 * 1000, // Consider data fresh for 1 hour
    gcTime: 60 * 60 * 1000, // Keep in garbage collection for 1 hour
  });

  const circuitData = circuitReportResponse?.data || [];

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setCircuitPage(0);
  };

  const comparisonData = data?.data || [];
  const totalCount = data?.count || comparisonData.length;

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredData = useMemo(() => {
    let filtered = comparisonData.filter((item) => {
      const s = item.removed_service;
      const searchStr = searchQuery.toLowerCase();
      return (
        s.service_id?.toLowerCase().includes(searchStr) ||
        s.service_name?.toLowerCase().includes(searchStr) ||
        s.doc_id?.toLowerCase().includes(searchStr) ||
        s.location_id?.toLowerCase().includes(searchStr) ||
        s.country?.toLowerCase().includes(searchStr) ||
        s.ref_id?.toLowerCase().includes(searchStr)
      );
    });

    return filtered.sort((a, b) => {
      const valA = a.removed_service[orderBy];
      const valB = b.removed_service[orderBy];
      if (valA < valB) return order === "asc" ? -1 : 1;
      if (valA > valB) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [comparisonData, searchQuery, order, orderBy]);

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadCSV = () => {
    if (!comparisonData.length) return;

    const headers = [
      "Removed Service ID",
      "Service Name",
      "Description",
      "Location ID",
      "Location Name",
      "Doc ID",
      "Quantity",
      "Commitment",
      "MRC",
      "NRC",
      "Conv. MRC USD",
      "Conv. NRC USD",
      "Currency",
      "Country",
      "Ref ID",
      "Row Flag",
      "Conversion Rate",
      "Match Count",
      "Matched Doc IDs",
    ];

    const csvRows = [headers.join(",")];

    comparisonData.forEach((item) => {
      const service = item.removed_service;
      const matchedDocs = item.matching_records.map((m) => m.doc_id).join("; ");
      const values = [
        `"${service.service_id}"`,
        `"${service.service_name || ""}"`,
        `"${service.service_description}"`,
        `"${service.location_id}"`,
        `"${service.location_name}"`,
        `"${service.doc_id}"`,
        service.quantity,
        `"${service.service_commitment}"`,
        service.mrc,
        service.nrc,
        service.converted_mrc_usd,
        service.converted_nrc_usd,
        `"${service.currency}"`,
        `"${service.country}"`,
        `"${service.ref_id}"`,
        `"${service.row_flag}"`,
        service.conversion_rate,
        item.match_count,
        `"${matchedDocs}"`,
      ];
      csvRows.push(values.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Removed_Services_Comparison_${dayjs().format("YYYYMMDD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadCircuitReportCSV = async () => {
    try {
      setDownloadingCircuitCSV(true);
      const response = await fetchCircuitReport({
        start_date: circuitStartDate,
        end_date: circuitEndDate,
        download_csv: true,
      });

      // Handle CSV download - response should be a blob or CSV content
      if (response instanceof Blob) {
        const url = URL.createObjectURL(response);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Circuit_Report_${dayjs().format("YYYYMMDD")}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // If response is text/data, create CSV manually
        const csvContent = typeof response === "string" ? response : JSON.stringify(response);
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Circuit_Report_${dayjs().format("YYYYMMDD")}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading circuit report CSV:", error);
    } finally {
      setDownloadingCircuitCSV(false);
    }
  };

  // Filter circuit data based on search
  const filteredCircuitData = useMemo(() => {
    if (!circuitData) return [];
    const searchStr = circuitSearchQuery.toLowerCase();
    return circuitData.filter((item) => {
      return (
        item["PIP Circuit ID"]?.toLowerCase().includes(searchStr) ||
        item["Service"]?.toLowerCase().includes(searchStr) ||
        item["Service ID"]?.toLowerCase().includes(searchStr) ||
        item["Office"]?.toLowerCase().includes(searchStr) ||
        item["Location"]?.toLowerCase().includes(searchStr)
      );
    });
  }, [circuitData, circuitSearchQuery]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          borderBottom: "1px solid #eee",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            Removed Services Comparison
            <Tooltip 
              title={
                <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Removed Services</Typography>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>Displays services marked with the REMOVED activity in SOF documents.</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Matching Records Found in Other SOF Documents</Typography>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>Displays records from other SOF documents that match the selected removed service based on Service ID, Service Name, Location ID, and Service Description. These matching records help identify whether the service still exists in another SOF document.</Typography>
                  </Box>
                </Box>
              } 
              arrow 
              placement="right"
            >
              <span style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <AiOutlineInfoCircle size={16} />
              </span>
            </Tooltip>
          </Box>
          {currentTab === 0 && (
            <TextField
              size="small"
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              sx={{
                minWidth: 200,
                "& .MuiInputBase-root": { height: "32px", fontSize: "13px" },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AiOutlineSearch size={18} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          {currentTab === 1 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Search circuit report..."
                value={circuitSearchQuery}
                onChange={(e) => {
                  setCircuitSearchQuery(e.target.value);
                  setCircuitPage(0);
                }}
                sx={{
                  minWidth: 200,
                  "& .MuiInputBase-root": { height: "32px", fontSize: "13px" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AiOutlineSearch size={18} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Start"
                type="date"
                size="small"
                value={circuitStartDate}
                onChange={(e) => setCircuitStartDate(e.target.value)}
                sx={{
                  width: 170,
                  "& .MuiInputBase-root": { height: "32px", fontSize: "13px" },
                }}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="End"
                type="date"
                size="small"
                value={circuitEndDate}
                onChange={(e) => setCircuitEndDate(e.target.value)}
                sx={{
                  width: 170,
                  "& .MuiInputBase-root": { height: "32px", fontSize: "13px" },
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Download CSV" placement="top" arrow>
            <IconButton
              onClick={
                currentTab === 0
                  ? handleDownloadCSV
                  : handleDownloadCircuitReportCSV
              }
              disabled={
                currentTab === 0
                  ? comparisonData.length === 0
                  : (circuitData && circuitData.length === 0) || downloadingCircuitCSV
              }
              sx={{
                backgroundColor: "#f5f5f5",
                "&:hover": { backgroundColor: "#e0e0e0" },
              }}
            >
              {currentTab === 1 && downloadingCircuitCSV ? (
                <CircularProgress size={20} sx={{ color: "#7C2EA0" }} />
              ) : (
                <PiFileCsv size={20} color="#333" />
              )}
            </IconButton>
          </Tooltip>
          {currentTab === 1 && (
            <Button 
              variant="contained" 
              size="small" 
              disabled={circuitLoading}
              onClick={() => {
                onClose();
                navigate("/circuit-dashboard", {
                  state: {
                    initialData: circuitData,
                    startDate: circuitStartDate,
                    endDate: circuitEndDate
                  }
                });
              }}
              sx={{ backgroundColor: "#7C2EA0", "&:hover": { backgroundColor: "#5f237a" }, textTransform: "none", minWidth: 130 }}
            >
              {circuitLoading ? <CircularProgress size={16} sx={{ color: "rgba(255, 255, 255, 0.7)" }} /> : "View Dashboard"}
            </Button>
          )}
          <IconButton onClick={onClose}>
            <AiOutlineClose />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* Tabs removed as there's only one view now */}

      <DialogContent sx={{ minHeight: "400px", pt: 2 }}>
        {/* Tab 1: Removed Services */}
        {currentTab === 0 && (
          <>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
                <CircularProgress sx={{ color: "#7C2EA0" }} />
              </Box>
            ) : filteredData.length > 0 ? (
              <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
                <TableContainer sx={{ maxHeight: "60vh" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", width: 50, zIndex: 11 }} />
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>
                          <TableSortLabel
                            active={orderBy === "service_id"}
                            direction={orderBy === "service_id" ? order : "asc"}
                            onClick={() => handleRequestSort("service_id")}
                            sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                          >
                            Service ID
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>
                          <TableSortLabel
                            active={orderBy === "service_name"}
                            direction={orderBy === "service_name" ? order : "asc"}
                            onClick={() => handleRequestSort("service_name")}
                            sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                          >
                            Service Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Description</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Location ID</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Doc ID</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Qty</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Commitment</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>MRC</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>NRC</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>USD MRC</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>USD NRC</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Conversion Rate</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Cur</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Country</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>SOF Date</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Ref ID</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Flag</TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Activity</TableCell>
                        <TableCell align="right" sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Matches</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.map((item, idx) => (
                        <ServiceRow key={item.removed_service.id || item.removed_service.service_id || idx} item={item} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            ) : (
              <Box sx={{ p: 8, textAlign: "center" }}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ fontStyle: "italic" }}
                >
                  {searchQuery ? "No results matching your search." : "No removed services found."}
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Tab 2: Circuit Report */}
        {/* currentTab === 1 && (
          <>
            {circuitLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
                <CircularProgress sx={{ color: "#7C2EA0" }} />
              </Box>
            ) : filteredCircuitData && filteredCircuitData.length > 0 ? (
              <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
                <TableContainer sx={{ maxHeight: "60vh" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", width: 50, zIndex: 11 }} />
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="S.No." /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Office" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Location" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="PIP Circuit ID" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Service ID" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Service" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Disconnect Request Number" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Is Removed" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Removed Date" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Removed SOF" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Ref From" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Ref Doc ID" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Ref Ref ID" tooltipKey="Ref Ref ID" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Submission Date" /></TableCell>
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Status" /></TableCell>
                        <TableCell align="right" sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}><HeaderWithTooltip label="Months" /></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCircuitData
                        .slice(
                          circuitPage * circuitRowsPerPage,
                          circuitPage * circuitRowsPerPage + circuitRowsPerPage
                        )
                        .map((item, idx) => (
                          <CircuitReportRow key={item["Service ID"] || idx} item={item} />
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={filteredCircuitData.length}
                  rowsPerPage={circuitRowsPerPage}
                  page={circuitPage}
                  onPageChange={(event, newPage) => setCircuitPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setCircuitRowsPerPage(parseInt(event.target.value, 10));
                    setCircuitPage(0);
                  }}
                />
              </Paper>
            ) : (
              <Box sx={{ p: 8, textAlign: "center" }}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ fontStyle: "italic" }}
                >
                  {circuitSearchQuery
                    ? "No results matching your search."
                    : "No circuit report data found."}
                </Typography>
              </Box>
            )}
          </>
        ) */}
      </DialogContent>
    </Dialog>
  );
};

export default RemovedServicesModal;
